export type PurityStatus = "unknown" | "estimated" | "tested";

export type PowderDose = {
  powder_mass_mg: number;
  purity_status: PurityStatus;
  purity_percent: number | null;
  estimated_active_mg: number | null;
};

function normalized(value: unknown) {
  return String(value ?? "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

export function isCocaine(value: unknown) {
  const name = normalized(value);
  return /(^|\s)(cocaina|cocaine|crack|cloridrato de cocaina)(\s|$)/.test(name);
}

function massToMg(amount: number, unit: string) {
  const key = normalized(unit);
  if (!Number.isFinite(amount) || amount <= 0) return null;
  if (key === "mg" || key === "miligrama" || key === "miligramas") return amount;
  if (key === "g" || key === "grama" || key === "gramas") return amount * 1000;
  if (key === "mcg" || key === "ug" || key === "micrograma" || key === "microgramas") return amount / 1000;
  return null;
}

export function calculatePowderDose(
  amount: number,
  unit: string,
  purityStatus: PurityStatus,
  purityPercent?: number | null,
): PowderDose | null {
  const powderMassMg = massToMg(amount, unit);
  if (powderMassMg == null) return null;
  if (purityStatus === "unknown") {
    return {
      powder_mass_mg: powderMassMg,
      purity_status: "unknown",
      purity_percent: null,
      estimated_active_mg: null,
    };
  }
  if (purityPercent == null || !Number.isFinite(purityPercent) || purityPercent <= 0 || purityPercent > 100) return null;
  return {
    powder_mass_mg: powderMassMg,
    purity_status: purityStatus,
    purity_percent: purityPercent,
    estimated_active_mg: powderMassMg * purityPercent / 100,
  };
}

export function formatPowderDose(value: PowderDose) {
  const powder = value.powder_mass_mg >= 1000
    ? `${(value.powder_mass_mg / 1000).toFixed(3).replace(/0+$/, "").replace(/\.$/, "")} g de material`
    : `${value.powder_mass_mg.toFixed(value.powder_mass_mg >= 100 ? 0 : 1)} mg de material`;
  if (value.purity_status === "unknown" || value.estimated_active_mg == null) {
    return `${powder} · pureza desconhecida · massa ativa não estimada`;
  }
  return `${powder} · ${value.purity_percent}% ${value.purity_status === "tested" ? "analisada" : "estimada"} · ≈ ${value.estimated_active_mg.toFixed(1)} mg de princípio ativo`;
}

export function activeMassForModel(dose: any): { amount: number; unit: string } | null {
  const name = dose?.substance_name ?? dose?.substances?.name ?? dose?.canonicalName ?? "";
  if (!isCocaine(name)) {
    const amount = Number(dose?.dose_amount);
    if (!Number.isFinite(amount) || amount <= 0) return null;
    return { amount, unit: String(dose?.dose_unit ?? "mg") };
  }

  const explicit = Number(dose?.estimated_active_mg);
  if (Number.isFinite(explicit) && explicit > 0) return { amount: explicit, unit: "mg" };

  const status = String(dose?.purity_status ?? "");
  const mass = Number(dose?.powder_mass_mg);
  const purity = Number(dose?.purity_percent);
  if ((status === "estimated" || status === "tested") && Number.isFinite(mass) && mass > 0 && Number.isFinite(purity) && purity > 0 && purity <= 100) {
    return { amount: mass * purity / 100, unit: "mg" };
  }
  return null;
}
